import { snapdom } from '@zumer/snapdom'

const SCALE = 2

const SUBHEADER = '.table-island__header-group.second'

const PERIOD_HEADER = '.table-island__header-group.first'

const LEADING_LABELS = ['Metric', 'Outcome', 'Stage']

const UNCLIPPED_PROPERTIES = ['overflow', 'max-height', 'height', 'min-width'] as const

type RestoredStyle = { element: HTMLElement; styles: Record<string, string> }

const rendered = (element: Element): boolean => element.getClientRects().length > 0

const visibleCells = (row: Element): string[] =>
  Array.from(row.children)
    .filter(rendered)
    .map(cell => cell.textContent?.trim() ?? '')

const NEEDS_QUOTES = /[",\r\n]/

const escapeField = (value: string): string =>
  NEEDS_QUOTES.test(value) ? `"${value.replace(/"/g, '""')}"` : value

const unique = (names: string[]): string[] => {
  const seen = new Map<string, number>()

  return names.map(name => {
    const count = seen.get(name) ?? 0

    seen.set(name, count + 1)

    return count ? `${name} (${count + 1})` : name
  })
}

export async function downloadPanel(element: HTMLElement, format: string, filename: string): Promise<void> {
  if (format === 'csv') {
    const csv = buildCsv(element)
    if (csv === null) return

    saveBlob(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' }), filename)

    return
  }

  saveBlob(await captureImage(element, format), filename)
}

export async function captureImage(element: HTMLElement, format: string): Promise<Blob> {
  const canvas = await snapshot(element)
  const mime = format === 'jpg' ? 'image/jpeg' : 'image/png'
  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, mime, format === 'jpg' ? 1 : undefined)
  )

  if (!blob) throw new Error(`${format} produced no blob`)

  return blob
}

const buildCsv = (element: HTMLElement): string | null => {
  const head = element.querySelector('table')?.tHead
  if (!head) return null

  const subRow = head.querySelector<HTMLTableRowElement>(SUBHEADER) ?? head.rows[head.rows.length - 1]
  if (!subRow) return null

  const periods = head.querySelector<HTMLTableRowElement>(PERIOD_HEADER)
  const periodLabels = periods ? visibleCells(periods).slice(LEADING_LABELS.length) : []
  const dataLabels = visibleCells(subRow).slice(LEADING_LABELS.length)
  const perPeriod = periodLabels.length ? Math.max(1, Math.round(dataLabels.length / periodLabels.length)) : dataLabels.length
  const headers = unique([
    ...LEADING_LABELS,
    ...dataLabels.map((label, index) => {
      const period = periodLabels[Math.floor(index / perPeriod)]

      return period ? `${period} - ${label}` : label
    })
  ])

  const rows = Array.from(element.querySelectorAll('tbody tr'))
    .filter(rendered)
    .map(row => visibleCells(row))
  const width = headers.length
  const body = rows.map(row => Array.from({ length: width }, (_, index) => row[index] ?? ''))

  return [headers, ...body].map(row => row.map(escapeField).join(',')).join('\r\n')
}

const backdrop = (element: HTMLElement): string => {
  const { backgroundColor } = getComputedStyle(element)

  return backgroundColor === 'rgba(0, 0, 0, 0)' ? '#ffffff' : backgroundColor
}

const saveBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

async function snapshot(element: HTMLElement): Promise<HTMLCanvasElement> {
  const unclipped = unclipScrollers(element)

  try {
    return await snapdom.toCanvas(element, { backgroundColor: backdrop(element), scale: SCALE })
  } finally {
    unclipped.forEach(({ element: node, styles }) => {
      Object.entries(styles).forEach(([property, value]) => {
        if (value === null) node.style.removeProperty(property)
        else node.style.setProperty(property, value)
      })
    })
  }
}

const unclipScrollers = (root: HTMLElement): RestoredStyle[] => {
  const restored: RestoredStyle[] = []
  const isScroller = (element: HTMLElement): boolean => {
    const { overflowX, overflowY } = getComputedStyle(element)

    return ['auto', 'scroll'].includes(overflowX) || ['auto', 'scroll'].includes(overflowY)
  }

  const unclip = (element: HTMLElement): void => {
    if (restored.some(entry => entry.element === element)) return

    const styles = Object.fromEntries(UNCLIPPED_PROPERTIES.map(property => [property, element.style.getPropertyValue(property)]))

    restored.push({ element, styles })
    element.style.setProperty('overflow', 'visible', 'important')
    element.style.setProperty('max-height', 'none', 'important')
    element.style.setProperty('height', 'auto', 'important')
    element.style.setProperty('min-width', `${element.scrollWidth}px`, 'important')
  }

  Array.from(root.querySelectorAll<HTMLElement>('*')).filter(isScroller).forEach(unclip)

  if (root.scrollWidth > root.clientWidth) unclip(root)

  return restored
}
